import { useIntl } from "react-intl";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { useMemo, useState } from "react";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import getColumns from "./columns";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import { TableHeaderFilter } from "@lib/types/Api";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { type TableProps } from "antd";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { SharedContextProps } from "@lib/types";
import { XAILeadDrawer } from "@lib/components/PageComponents/XAILead";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  apiGetXAILeads,
  apiDeleteXAILead,
  apiUpdateXAILead,
} from "@lib/api/xAI";
import { XAILead, XAICallStatus } from "@lib/types/xAI";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { ActionButton } from "@lib/components/MiniComponents/Buttons";
import { IoMdClose } from "react-icons/io";
import { TbListDetails } from "react-icons/tb";
import TftSalesLeadCalls from "./tftSalesLeadCalls";

const SharedTftSalesLeads: React.FC<SharedContextProps> = ({
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  minusHeight = 0,
  tableProps,
  height,
  queryParams,
  detailContext,
  customerId,
}) => {
  const navigate = useNavigate();
  const { messages } = useIntl();
  const [openModal, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<XAILead | null>(null);

  const toggle = (status?: boolean) => {
    if (typeof status !== "undefined") setModalOpen(status);
    else setModalOpen(!openModal);
  };

  const handleSelect = (item: XAILead) => {
    setSelected(item);
    toggle(true);
  };

  const initialFilters = [
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
  ];

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
    join: ["customer"],
    filter: initialFilters,
    ...queryParams,
  });

  const leadsQuery = useQuery({
    queryKey: ["tft-sales-leads", JSON.stringify(params)],
    queryFn: () => apiGetXAILeads(params),
    enabled: !!params && !!pageIsActive,
  });

  const { data, isLoading, isFetching, refetch } = leadsQuery;
  const apiData = data?.data?.data;

  const deleteMutation = useMutation({
    mutationFn: apiDeleteXAILead,
    onSuccess() {
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["sidebar.tftSalesLeads"] as string,
        ),
      );
      refetch();
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const handleDelete = (item: XAILead) => {
    deleteMutation.mutate(item.id);
  };

  const resendMutation = useMutation({
    mutationFn: apiUpdateXAILead,
    onSuccess() {
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["sidebar.tftSalesLeads"] as string,
        ),
      );
      refetch();
    },
    onError() {
      toast.error(messages["action.error"] as string);
    },
  });

  const handleResend = (item: XAILead) => {
    resendMutation.mutate({ id: item.id, callStatus: XAICallStatus.Pending });
  };

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ ...params, page, limit });
    }
  };

  const onChange: TableProps<XAILead>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const columns = useMemo(
    () =>
      getColumns({
        navigate,
        apiData,
        params,
        messages,
        handleSelect,
        handleDelete,
        handleResend,
        columnsConfig: tableProps?.columnsConfig,
        detailContext,
      }),
    [
      apiData,
      params,
      tableProps?.columnsConfig,
      messages,
      handleSelect,
      handleDelete,
      handleResend,
      detailContext,
      navigate,
    ],
  );

  const hiddenFilters = useMemo(() => {
    if (!!detailContext) {
      return ["customerId"];
    }
    return [];
  }, [detailContext]);

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => !hiddenFilters.includes(f.key)) as TableHeaderFilter[];
  }, [params?.filter, columns, hiddenFilters]);

  const title = customTitle || (messages["sidebar.tftSalesLeads"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      minusHeight={minusHeight}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={leadsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={{ onChange, columns, ...tableProps }}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          expandable={{
            // showExpandColumn: false,
            columnTitle: "#",
            columnWidth: "50px",
            fixed: "right",
            expandIcon: ({ expanded, record, onExpand }) => (
              <ActionButton
                type={expanded ? "close" : "detail"}
                icon={expanded ? <IoMdClose /> : <TbListDetails />}
                color={expanded ? "red" : "green"}
                onClick={(e) => onExpand(record, e)}
                noLabel
              />
            ),
            expandedRowRender: (record) => <TftSalesLeadCalls item={record} />,
            onExpand(expanded, record) {
              try {
                const find = document.querySelector(
                  `[data-row-key="${record.id}"]`,
                );
                if (find) {
                  find.setAttribute(
                    "data-row-expanded",
                    expanded ? "true" : "false",
                  );
                }
              } catch (error) {}
            },
          }}
          {...tableProps}
        />
      </AppsContent>

      <XAILeadDrawer
        open={openModal}
        onClose={() => toggle(false)}
        item={selected}
      />
    </AppsContainer>
  );
};

export default SharedTftSalesLeads;
